"use client";

import { useState, useRef, useEffect } from "react";
import { htmlElementToMarkdown } from "../lib/html-to-markdown";

interface CopyMarkdownButtonProps {
  markdown?: string;
}

export default function CopyMarkdownButton({ markdown }: CopyMarkdownButtonProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const getMarkdown = (): string => {
    if (markdown) return markdown;
    const proseEl = document.getElementById("docs-prose");
    if (proseEl) return htmlElementToMarkdown(proseEl);
    return "";
  };

  const handleCopy = async () => {
    try {
      const text = getMarkdown();
      if (text) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        setOpen(false);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleViewMarkdown = () => {
    const text = getMarkdown();
    if (text) {
      const blob = new Blob([text], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
    setOpen(false);
  };

  const handleOpenIn = (provider: string) => {
    const pageUrl = window.location.href;
    const pageTitle = document.title;

    const prompt = `I'm reading the documentation for Code Pathfinder (https://codepathfinder.dev), an open-source SAST tool for finding security vulnerabilities in code. It features a Python SDK for writing custom rules, call graph analysis, MCP server integration, and AI-powered security scanning via SecureFlow.\n\nPlease summarize this page for me: ${pageUrl}\n\nPage title: ${pageTitle}`;

    const encoded = encodeURIComponent(prompt);

    const urls: Record<string, string> = {
      chatgpt: `https://chatgpt.com/?q=${encoded}`,
      claude: `https://claude.ai/new?q=${encoded}`,
      perplexity: `https://www.perplexity.ai/search?q=${encoded}`,
    };

    if (urls[provider]) {
      window.open(urls[provider], "_blank");
    }
    setOpen(false);
  };

  return (
    <div className="flex justify-end mb-2 relative" id="copy-markdown-btn" ref={menuRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-800 transition-all"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        {copied ? "Copied!" : "Copy page"}
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50 py-2 text-sm">
          {/* Copy & View */}
          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-200 hover:bg-gray-800 transition-colors text-left"
          >
            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy page as Markdown
            <svg className="w-3.5 h-3.5 text-gray-500 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>

          <button
            onClick={handleViewMarkdown}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-200 hover:bg-gray-800 transition-colors text-left"
          >
            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            View Markdown
            <svg className="w-3.5 h-3.5 text-gray-500 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>

          {/* Divider */}
          <div className="border-t border-gray-700 my-2" />

          {/* Open in providers */}
          <button
            onClick={() => handleOpenIn("chatgpt")}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-200 hover:bg-gray-800 transition-colors text-left"
          >
            <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
            </svg>
            Open in ChatGPT
            <svg className="w-3.5 h-3.5 text-gray-500 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>

          <button
            onClick={() => handleOpenIn("claude")}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-200 hover:bg-gray-800 transition-colors text-left"
          >
            <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4.709 15.955l4.72-2.756.08-.046 2.29-1.337 2.292-1.338.078-.046 4.722-2.756c.428-.25.927-.166 1.234.139l-7.09-5.47a1.903 1.903 0 00-2.07 0L3.876 7.815l.003.003c.328-.123.728-.03 1.028.183l-.198-.046zm10.861 2.83l4.72-2.756c.37-.216.596-.584.616-.977l-5.79 3.38-4.583 2.674-.003.002 1.804 1.391a1.903 1.903 0 002.07 0l1.166-.714zm5.344-4.167a1.244 1.244 0 00-.008-.434l-.003-.013a1.225 1.225 0 00-.06-.189l-.009-.02a1.218 1.218 0 00-.104-.19l-.012-.016a1.227 1.227 0 00-.148-.17 1.236 1.236 0 00-.29-.2L15.56 10.8l-2.29 1.338 4.722 2.756.078.046 2.29 1.337 1.6.934c.022-.253-.013-.522-.045-.593zM8.288 12.04l-2.29 1.337-4.722 2.757-.078.045-1.6-.933c-.021.252.014.521.046.592a1.244 1.244 0 00.008.434l.003.013c.016.065.036.128.06.189l.009.02a1.22 1.22 0 00.253.375c.085.08.18.148.282.2l4.72 2.756 2.292-1.337-4.723-2.757-.078-.045-2.29-1.338-1.6-.933c-.022-.253.013-.522.045-.593zm-.854-1.792L3.472 7.85a1.107 1.107 0 00-.616.977l5.79-3.38L13.23 2.77l.002-.002-1.804-1.39a1.903 1.903 0 00-2.07 0L8.193 2.09l-4.72 2.756L3.394 7.6l-.078.046L1.027 8.98c-.37.216-.596.584-.616.977l5.79-3.38-.078.046 2.29-1.338.02-.036zm4.072 1.951L9.216 13.54l-2.29 1.337-.08.047-4.72 2.756c-.429.25-.928.166-1.234-.14l7.09 5.47a1.903 1.903 0 002.07 0l7.09-5.47a1.233 1.233 0 01-.437.17 1.236 1.236 0 01-.797-.03l-.198.046-4.722-2.756-.078-.046-2.29-1.338-.078-.046z" />
            </svg>
            Open in Claude
            <svg className="w-3.5 h-3.5 text-gray-500 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>

          <button
            onClick={() => handleOpenIn("perplexity")}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-200 hover:bg-gray-800 transition-colors text-left"
          >
            <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.463 0H6.537L0 6.537v10.926L6.537 24h10.926L24 17.463V6.537L17.463 0zM12 19.2a7.2 7.2 0 110-14.4 7.2 7.2 0 010 14.4z" />
            </svg>
            Open in Perplexity
            <svg className="w-3.5 h-3.5 text-gray-500 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
